// OS / platform detection for marketing surfaces.
//
// Two consumers:
//   1. Server components on dynamic pages can call `getOsFromHeaders(headers())`
//      to detect platform from the User-Agent header at request time.
//   2. Client components running on static pages call the matching client-side
//      helper inside <PlatformProvider> (see lib/contexts/PlatformContext).
//
// What we detect:
//   - "mac"     — macOS (any architecture)
//   - "windows" — Windows
//   - "other"   — Linux / mobile / unknown. Treated as Windows for the purpose
//                 of keyboard-shortcut display (Ctrl) since Linux users use
//                 Ctrl, and unknown platforms default to the more conservative
//                 cross-OS label.
//
// What we deliberately don't try to do:
//   - Apple Silicon vs Intel Mac. Chrome on Apple Silicon still reports
//     "Intel Mac OS X 10_15_7" in the legacy UA string for compatibility, so
//     this is unreliable from headers alone. The download dropdown still
//     offers both options.
//   - iPhone/iPad/Android. Mobile users can't run the desktop app, so the
//     keyboard label fallback (Ctrl) is irrelevant — we just don't pretend
//     to detect them here.

export type OsPlatform = "mac" | "windows" | "other"

interface HeaderLookup {
  get(name: string): string | null
}

/**
 * Server-side detection. Reads the `User-Agent` header and returns the
 * visitor's platform. Defaults to "other" if the header is missing or
 * unrecognised — calling code should treat "other" as Ctrl (Windows-style)
 * for keyboard-shortcut labels.
 */
export function getOsFromHeaders(headers: HeaderLookup): OsPlatform {
  const ua = headers.get("user-agent") || ""
  return parseOsFromUserAgent(ua)
}

/**
 * Pure parser. Exported so the client-side fallback (<PlatformProvider>) can
 * reuse the exact same logic against `navigator.userAgent`.
 */
export function parseOsFromUserAgent(ua: string): OsPlatform {
  if (!ua) return "other"

  // iOS / Android / mobile — no native desktop app, no Cmd/Ctrl distinction
  // worth making here. Bucket as "other" so the Ctrl fallback applies.
  if (/iPhone|iPad|iPod|Android/i.test(ua)) {
    return "other"
  }

  // Windows: NT kernel string is the canonical marker; Win64/Win32 are
  // architecture flags that always co-occur with NT but we match either.
  if (/Windows NT|Win64|Win32/i.test(ua)) {
    return "windows"
  }

  // macOS: legacy "Mac OS X" or modern "macOS" or just "Macintosh". Includes
  // "Intel Mac OS X" UAs that Chrome/Apple Silicon still emit for
  // compatibility — that's fine, we treat them as Mac uniformly.
  if (/Macintosh|Mac OS X|macOS|Mac_PowerPC/i.test(ua)) {
    return "mac"
  }

  return "other"
}

/**
 * Modifier-key label for the visitor's platform. Used by the marketing site
 * to show "⌘ + I" to Mac visitors and "Ctrl + I" to everyone else.
 */
export function getCommandKeySymbol(os: OsPlatform): string {
  return os === "mac" ? "⌘" : "Ctrl"
}

/**
 * Compact hotkey style (no spaces) used in keyboard-hint pills like
 * "⌘G" / "⌘↵" inside the desktop-app preview screenshots. Mac stays
 * compact ("⌘G") because the symbol is one glyph; Windows needs the
 * separator ("Ctrl+G") so "CtrlG" doesn't end up as one word.
 */
export function formatCompactHotkey(os: OsPlatform, key: string): string {
  return os === "mac" ? `⌘${key}` : `Ctrl+${key}`
}

/**
 * Spaced hotkey style ("⌘ + I" / "Ctrl + I") used in marketing copy.
 * Convenience wrapper so callers don't repeat the " + " concatenation.
 */
export function formatSpacedHotkey(os: OsPlatform, key: string): string {
  return `${getCommandKeySymbol(os)} + ${key}`
}
